import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import classnames from 'classnames';
import { useGetTakeRateStatus } from '@/query/queries/reporting/useGetTakeRateStatus';
import { LS_REPORTING_MODE_KEY, REPORTING_MODES } from '@/helpers/reporting';
import { APP_LINKS } from '@/helpers/links';
import { prettyDate } from '@/helpers/date';
import { ReportingModeModal } from '@/modals/ReportingModeModal';
import { useGetMe } from '@/hooks/shared/useGetMe';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { useModal } from '@/providers/modal';
import Icon from './icon_calendar.svg';
import s from './index.module.scss';

export const ReportingMode = ({ className }) => {
  const { openSingleModal } = useModal();
  const { pathname } = useLocation();
  const { isMainFounder } = useGetMe();
  const { data: status } = useGetTakeRateStatus();
  const { t } = useTranslation('reporting');

  const isReportingPage = pathname === APP_LINKS.reporting;

  const onManage = useCallback(() => {
    openSingleModal(MODAL_IDS.reporting_mode);
  }, [openSingleModal]);

  const getTitle = useCallback(() => {
    if (!status) return '';
    if (status.reporting_mode === REPORTING_MODES.TRANSPARENT)
      return t('reporting_mode.transparent_title');
    if (status.reporting_mode === REPORTING_MODES.OPAQUE) return t('reporting_mode.opaque_title');
  }, [status]);

  const getDescription = useCallback(() => {
    if (!status) return '';
    if (!isReportingPage)
      return t('reporting_mode.current_mode', { period_months: status.period_months });

    if (status.reporting_mode === REPORTING_MODES.TRANSPARENT)
      return t('reporting_mode.transparent_description', { period_months: status.period_months });

    if (
      status.reporting_mode === REPORTING_MODES.OPAQUE &&
      status.next_reporting_mode === REPORTING_MODES.TRANSPARENT &&
      status.next_reporting_mode_from
    )
      return t('reporting_mode.switch_to_transparent', {
        date: prettyDate(status.next_reporting_mode_from, 'MMMM DD'),
      });

    if (status.reporting_mode === REPORTING_MODES.OPAQUE)
      return t('reporting_mode.opaque_description', { period_months: status.period_months });
  }, [isReportingPage, status]);

  useEffect(() => {
    if (status) {
      const isFirstTime = localStorage.getItem(LS_REPORTING_MODE_KEY) !== '1';
      isFirstTime && onManage();
    }
  }, [status, onManage]);

  if (!status) return null;

  return (
    <>
      <div className={classnames(s.wrapper, isReportingPage && s.wrapper_reporting, className)}>
        <div className={s.icon}>
          <img src={Icon} alt="icon" width={20} height={20} />
        </div>
        <div className={s.content}>
          <div className={s.title}>{getTitle()}</div>
          <div className={s.desc}>{getDescription()}</div>
        </div>
        {isMainFounder && (
          <button
            type="button"
            className={classnames(s.btn_manage, isReportingPage && s.btn_manage_grey)}
            onClick={onManage}
          >
            {isReportingPage
              ? t('reporting_mode.change_mode')
              : t('reporting_mode.manage_reporting')}
          </button>
        )}
      </div>
      {isMainFounder && <ReportingModeModal />}
    </>
  );
};
